import { CheckCircle2, FileText, Handshake, Shield, BookOpen } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { PageHeader } from "../components/shared/PageHeader";
import { CTASection } from "../components/shared/CTASection";
import { IMAGES } from "../data/content";

const CAPABILITIES = [
  { icon: FileText, title: "Capability statement development", desc: "Sharp, credible capability statements that position you for public-sector opportunities." },
  { icon: Handshake, title: "Proposal & readiness support", desc: "Structured support to prepare and strengthen proposals and contract readiness." },
  { icon: Shield, title: "Contract opportunity positioning", desc: "Positioning aviation organizations for government-related contract opportunities, including U.S. capability where applicable." },
  { icon: BookOpen, title: "SOP, manual & compliance documentation", desc: "Development of SOPs, manuals and safety/security documentation to public-sector standards." },
];

export default function Government() {
  return (
    <div data-testid="government-page">
      <PageHeader
        testId="government-header"
        eyebrow="Government Capability"
        title="Built for public-sector readiness and the documentation that earns trust."
        subtitle="A combined service line covering government-facing positioning and the critical aviation documentation your organization depends on."
        image={IMAGES.airport}
      />

      <section className="bg-background py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid gap-14 lg:grid-cols-12 lg:items-center">
            <div className="lg:col-span-6">
              <Reveal>
                <h2 className="font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl text-balance">
                  Positioning for government-related aviation work
                </h2>
                <p className="mt-6 text-base leading-relaxed text-muted-foreground lg:text-lg">
                  Public-sector engagements demand a distinct standard of preparation, documentation
                  and credibility. We help aviation organizations meet that bar — from capability
                  statements and proposals to the SOPs, manuals and compliance documentation that
                  underpin trust.
                </p>
                <ul className="mt-8 space-y-3">
                  {["Capability statement development", "Proposal & readiness support", "Contract opportunity positioning", "SOP / manual development", "Compliance documentation support", "Safety & security manual support"].map((t) => (
                    <li key={t} className="flex items-start gap-3 text-sm font-medium text-navy">
                      <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-brand" /> {t}
                    </li>
                  ))}
                </ul>
              </Reveal>
            </div>
            <div className="lg:col-span-6">
              <Reveal delay={0.15}>
                <img src={IMAGES.tower} alt="Airport control tower" className="h-96 w-full rounded-3xl object-cover lg:h-[500px]" />
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-secondary py-20 lg:py-24">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid gap-6 md:grid-cols-2">
            {CAPABILITIES.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.06}>
                <div className="flex h-full items-start gap-5 rounded-2xl border border-border bg-white p-8">
                  <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-aero/10 text-aero">
                    <c.icon className="h-6 w-6" />
                  </span>
                  <div>
                    <h3 className="font-display text-lg font-bold text-navy">{c.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{c.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
